import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { DealCreateInterface } from '@zaoblako/shared/modules/business/dto/deal/deal.create.dto';
import { DealFormDto } from '@zaoblako/shared/modules/business/dto/deal/deal.form.dto';
import type { DealsGroupFindInterface } from '@zaoblako/shared/modules/business/dto/deals-group/deals-group-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file-find.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { FileService } from '@zaoblako/web/core/services/file.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { DealOrderFormComponent } from '@zaoblako/web/modules/deal/dumbs/deal-order-form/deal-order-form.component';
import { FilesListComponent } from '@zaoblako/web/shared/components/files-list/files-list.component';
import { GoBackButtonComponent } from '@zaoblako/web/shared/components/go-back-button/go-back-button.component';
import { ContragentAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/contragent-auto-complete/contragent-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { HdTaskMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/hd-task-multi-select/hd-task-multi-select.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { PrioritySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/priority-single-select/priority-single-select.component';
import { ProjectMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/project-multi-select/project-multi-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { UserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-single-select/user-single-select.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';

@Component({
  standalone: true,
  selector: 'crm-deal-form',
  templateUrl: './deal-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    InputNumberModule,
    SingleSelectComponent,
    ContragentAutoCompleteComponent,
    EditorComponent,
    ButtonModule,
    GoBackButtonComponent,
    FormsModule,
    MultiSelectComponent,
    DirectionSingleSelectComponent,
    FilesListComponent,
    FileUploadModule,
    PrioritySingleSelectComponent,
    HdTaskMultiSelectComponent,
    ProjectMultiSelectComponent,
    UnitSingleSelectComponent,
    FilialSingleSelectComponent,
    DealOrderFormComponent,
    UserMultiSelectComponent,
    DatepickerComponent,
    UserSingleSelectComponent,
    DividerModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DealFormComponent extends BaseFormComponent<DealCreateInterface> {
  @Input() public deal: DealCreateInterface = {
    name: '',
    orders: [],
    contragent: null
  };

  public dto = DealFormDto;
  public engine: 'class-transformer' | 'serde' = 'serde';

  @Input() public groups: DealsGroupFindInterface[] = [];
  @Input() public userGroups: UserGroupFindInterface[] = [];
  @Input() public directions: DirectionFindInterface[] = [];
  @Input() public companies: CompanyFindInterface[] = [];
  @Input() public tasks: HelpdeskTaskFindInterface[] = [];
  @Input() public projects: ProjectFindInterface[] = [];

  @ViewChild('fileUpload') public fileUpload: FileUpload;
  public filesLoading = false;

  public constructor(
    public readonly globalService: GlobalService,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly fileService: FileService
  ) {
    super();
  }

  public uploadFiles(data: { files: FileList }) {
    this.filesLoading = true;
    this.cdRef.markForCheck();

    this.fileService.upload(data.files).subscribe({
      next: (files) => {
        this.deal.files = this.deal.files?.length ? this.deal.files.concat(files) : files;
        this.fileUpload.clear();
        this.filesLoading = false;
        this.cdRef.markForCheck();
      },
      error: () => {
        this.filesLoading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public deleteFile(files: FileFindInterface[], file: FileFindInterface) {
    this.confirmDialogService.confirm({
      message: `Вы действительно хотите удалить файл "${file.name}"?`,
      accept: () => {
        const index = files.indexOf(file);
        files.splice(index, 1);
        this.cdRef.markForCheck();
      }
    });
  }
}
